import * as Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsData from 'highcharts/modules/data';
import HighchartsSolidGauge from 'highcharts/modules/solid-gauge';
import { SeriesGaugeOptions } from 'highcharts';


///
/// EAP2 Widget is from when they made major updates to the notification system. Around 2026ish
///


// Initialize Highcharts modules - handle both default and direct exports
const initModule = (mod: any, hc: any) => {
    if (typeof mod === 'function') {
        mod(hc);
    } else if (mod && typeof mod.default === 'function') {
        mod.default(hc);
    }
};

initModule(HighchartsMore, Highcharts);
initModule(HighchartsData, Highcharts);
initModule(HighchartsSolidGauge, Highcharts);


async function getData(serviceUrl: string) {
    const r = await fetch(serviceUrl);
    return await r.json();
}

export function GridConditionsEAP2Widget(blockID: string | HTMLElement, serviceUrl: string) {
    getData(serviceUrl)
        .then(data => drawChart(blockID, serviceUrl, data?.condition, data?.titleString))
        .catch(() => drawChart(blockID, serviceUrl, "error"));
}

// allow external cleanup if needed
export function DisposeGridConditionsWidget(blockID: string | HTMLElement) {
    const key = typeof blockID === 'string' ? blockID : blockID.id;
    const intervalId = widgetIntervals[key];
    if (intervalId) {
        clearInterval(intervalId);
        delete widgetIntervals[key];
    }
    const resizeHandler = widgetResizeHandlers[key];
    if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
        delete widgetResizeHandlers[key];
    }
}

const widgetIntervals: Record<string, number> = {};
const widgetResizeHandlers: Record<string, () => void> = {};

const LEGEND_HTML = `
<div style="font-family: Lato, Arial, Helvetica, sans-serif; font-size: 13px; margin-top: 20px; display: flex; flex-direction: column; align-items: center;">
  <div style="width: fit-content;">
    <div style="display: flex; align-items: center; margin-bottom: 6px;">
      <span style="display: inline-block; width: 11px; height: 11px; background-color: #FFC844; border-radius: 50%; margin-right: 8px;"></span>
      Changing grid conditions; increased monitoring
    </div>
    <div style="display: flex; align-items: center; margin-bottom: 6px;">
      <span style="display: inline-block; width: 11px; height: 11px; background-color: #ee7623; border-radius: 50%; margin-right: 8px;"></span>
      Elevated reliability risk; opertional actions underway
    </div>
    <div style="display: flex; align-items: center; margin-bottom: 6px;">
      <span style="display: inline-block; width: 11px; height: 11px; background-color: #E74F3D; border-radius: 50%; margin-right: 8px;"></span>
      Emergency actions underway to protect reliability
    </div>
    <div style="display: flex; align-items: center;">
      <span style="display: inline-block; width: 11px; height: 11px; background-color: #B2292E; border-radius: 50%; margin-right: 8px;"></span>
      Power interruptions possible or occuring
    </div>
  </div>
</div>
`;

function resolveContainer(blockID: string | HTMLElement): HTMLElement | null {
    if (typeof blockID === 'string') return document.getElementById(blockID);
    return blockID;
}

function drawChart(blockID: string | HTMLElement, serviceUrl: string, response: string = "null", titleString: string = "") {
    const container = resolveContainer(blockID);
    if (!container) {
        return;
    }
    const key = typeof blockID === 'string' ? blockID : (blockID.id || `grid-conditions-${Date.now()}`);

    if (!chartLookup[response]) {
        response = "null";
    }

    const BASE = { dialBaseWidth: 38, pivotRadius: 17, plotBandThickness: 60, tickLength: 64 };
    function calcScale(width: number) { if (!width || width <= 0) return 0.7; return Math.min(1, Math.max(0.5, width / 520)); }
    let scale = calcScale(container.clientWidth);

    function primaryGauge(scale: number): SeriesGaugeOptions {
        return {
            type: "gauge", enableMouseTracking: false, data: [chartLookup[response]], showInLegend: false,
            overshoot: 0, wrap: false, clip: false,
            dial: { radius: '80%', backgroundColor: '#636569', baseWidth: Math.round(BASE.dialBaseWidth * scale), borderWidth: 3, borderColor: '#FFF', baseLength: '0%', rearLength: '0%', topWidth: 0 },
            pivot: { radius: Math.round(BASE.pivotRadius * scale), borderWidth: 3, borderColor: "#FFF", backgroundColor: "#636569" }
        };
    }

    function plotBands(scale: number) {
        const t = Math.round(BASE.plotBandThickness * scale);
        return [
            { from: 0, to: 20, outerRadius: "99%", color: '#7fbc42', thickness: t, className: "gauge1 insetShadowSVG" },
            { from: 20, to: 40, outerRadius: "99%", color: '#FFC844', thickness: t, className: "gauge2 insetShadowSVG" },
            { from: 40, to: 60, outerRadius: "99%", color: '#ee7623', thickness: t, className: "gauge3 insetShadowSVG" },
            { from: 60, to: 80, outerRadius: "99%", color: '#E74F3D', thickness: t, className: "gauge4 insetShadowSVG" },
            { from: 80, to: 100, outerRadius: "99%", color: '#B2292E', thickness: t, className: "gauge5 insetShadowSVG" }
        ];
    }

    function computeHeight(scale: number): number {
        return Math.max(180, Math.round(290 * scale));  
    }

    const gaugeSeries = primaryGauge(scale);

    function setTitleContent(alertStr: string, alertElement: HTMLElement) {
        if (!alertStr) {
            alertElement.innerHTML = '';
            return false;
        }
        const parts = alertStr.split('~').map(p => p.trim());
        let html = '';
        let hasShowLegend = false;

        const processPart = (part: string, index: number) => {
            if (part.toLowerCase() === "showlegend") {
                hasShowLegend = true;
                return '';
            }
            if (index === 0) {
                return `<h3>${part}</h3>`;
            }
            if (index === 1) {
                return `<h4>${part}</h4>`;
            }
            if (index === 2) {
                return `<h4>${part}</h4>`;
            }
            return `${part}`;
        };

        if (parts[0]) html += processPart(parts[0], 0);
        if (parts[1]) html += processPart(parts[1], 1);
        if (parts[2]) html += processPart(parts[2], 2);

        alertElement.innerHTML = html;
        return hasShowLegend;
    }

    const chart = Highcharts.chart(container, {
        accessibility: { enabled: false },
        title: { text: null },
        chart: {
            backgroundColor: 'transparent',
            height: computeHeight(scale),
            spacingTop: 0,
            marginTop: 0,
            spacingRight: 0,
            spacingBottom: 5,
            spacingLeft: 0,
            marginBottom: 10,
            reflow: true,
            style: { fontFamily: 'Lato, Arial, Helvetica, sans-serif' },
            events: {
                load: function () {
                    // Prevent SVG clipping of dial at extreme angles
                    const svgEl = this.container?.querySelector('svg');
                    if (svgEl) svgEl.style.overflow = 'visible';
                    setTimeout(this.reflow.bind(this), 30);
                    const intervalId = window.setInterval(() => {
                        $.getJSON(serviceUrl, (data) => {
                            const newResp = chartLookup[data.condition] ? data.condition : response;
                            response = newResp;
                            //alertString = data.alertString || "";
                            titleString = data.titleString || "";
                            chart.series[0].setData([chartLookup[response]], false);

                            const titleEl = container.querySelector('.grid-conditions-title') as HTMLElement;
                            const titleContentEl = titleEl?.querySelector('.grid-conditions-title-content') as HTMLElement;
                            const legendEl = titleEl?.querySelector('.grid-conditions-legend') as HTMLElement;
                            if (titleContentEl) {
                                const hasLegendInTitle = setTitleContent(titleString, titleContentEl);
                                if (legendEl) {
                                    legendEl.style.display = hasLegendInTitle ? 'block' : 'none';
                                }
                            }

                            const idPart = typeof blockID === 'string' ? blockID : blockID.id;
                            $(`#${idPart}additionalLink`).css("display", response !== 'green' ? "block" : "none");
                            chart.redraw();
                        }).fail(() => {
                        });
                    }, 300000);
                    widgetIntervals[key] = intervalId;
                }
            }
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ['50%', scale < 0.7 ? '88%' : '90%'],
            size: scale < 0.7 ? '170%' : '180%'
        },
        yAxis: {
            min: 0,
            max: 100,
            plotBands: plotBands(scale),
            tickPositions: [0, 20, 40, 60, 80, 100],
            tickLength: Math.round(BASE.tickLength * scale),
            tickWidth: 3,
            tickColor: "#fff",
            minorTickInterval: null,
            labels: { enabled: false },
            lineWidth: 0
        },
        xAxis: { accessibility: { rangeDescription: '' } },
        legend: {
            enabled: false
        },
        plotOptions: {
            series: {
                animation: { duration: 300 },
                dataLabels: { enabled: false }
            }
        },
        series: [
            gaugeSeries
        ]
    });

    const resizeHandler = debounce(() => {
        const w = container.clientWidth;
        const newScale = calcScale(w);
        if (Math.abs(newScale - scale) < 0.03) return;
        scale = newScale;

        chart.update({
            pane: {
                center: ['50%', scale < 0.7 ? '88%' : '90%'],
                size: scale < 0.7 ? '170%' : '180%'
            },
            chart: {
                height: computeHeight(scale),
                spacingTop: 0,
                marginTop: 0,
                spacingBottom: 5,
                marginBottom: 10
            },
            yAxis: {
                plotBands: plotBands(scale),
                tickLength: Math.round(BASE.tickLength * scale)
            }
        }, false);

        chart.series[0].update(primaryGauge(scale), false);
        chart.redraw();
    }, 120);

    window.addEventListener('resize', resizeHandler);
    widgetResizeHandlers[key] = resizeHandler;
    (container as any).__gridConditionsChart = chart;

    // Add title if not already present
    let titleEl = container.querySelector('.grid-conditions-title') as HTMLElement;
    if (!titleEl) {
        titleEl = document.createElement('div');
        titleEl.className = 'grid-conditions-title';
        titleEl.style.textAlign = 'center';
        titleEl.style.fontFamily = 'Lato, Arial, Helvetica, sans-serif';
        titleEl.style.fontSize = '14px';
        titleEl.style.marginTop = '10px';
        container.appendChild(titleEl);
    }

    // Create a separate container for title content (separate from legend)
    let titleContentEl = titleEl.querySelector('.grid-conditions-title-content') as HTMLElement;
    if (!titleContentEl) {
        titleContentEl = document.createElement('div');
        titleContentEl.className = 'grid-conditions-title-content';
        titleEl.insertBefore(titleContentEl, titleEl.firstChild);
    }

    const hasLegendInTitle = setTitleContent(titleString, titleContentEl);

    // Add legend only if explicitly requested
    let legendEl = titleEl.querySelector('.grid-conditions-legend') as HTMLElement;
    if (hasLegendInTitle) {
        if (!legendEl) {
            legendEl = document.createElement('div');
            legendEl.className = 'grid-conditions-legend';
            titleEl.appendChild(legendEl);
        }
        legendEl.innerHTML = LEGEND_HTML;
        legendEl.style.display = 'block';

        const idPart = typeof blockID === 'string' ? blockID : blockID.id;
        $(`#${idPart}additionalLink`).css("display", "none");
    } else if (legendEl) {
        legendEl.style.display = 'none';
    }
}

function debounce(fn: () => void, wait: number) {
    let t: any;
    return () => {
        clearTimeout(t);
        t = setTimeout(fn, wait);
    };
}

const chartLookup: Record<string, any> = {
    "null": {
        y: 0,
        dataLabels: { enabled: false },
        color: "#000"
    },
    "green": {
        y: 10,
        dataLabels: { enabled: false },
        color: "#000"
    },
    "yellow": {
        y: 30,
        dataLabels: { enabled: false },
        color: "#000"
    },
    "orange": {
        y: 50,
        dataLabels: { enabled: false },
        color: "#ee7623"
    },
    "lightRed": {
        y: 70,
        dataLabels: { enabled: false },
        color: "#E74F3D"
    },
    "darkRed": {
        y: 90,
        dataLabels: { enabled: false },
        color: "#B2292E"
    },
    "error": {
        y: 0,
        dataLabels: { enabled: false },
        color: "#000"
    }
};